import { useCallback, useEffect, useSyncExternalStore } from "react";
import { atributos } from "@/lib/preferencias.mjs";
import { creaAlmacen } from "@/lib/almacenDePreferencias.mjs";
import { sound } from "@/lib/sound";

/**
 * Las preferencias del alumno, colgadas del `<html>` para que mande el CSS.
 *
 * De momento viven en `localStorage` y no en el servidor, y hay un motivo para que sea el
 * primer sitio y no el segundo: **quien necesita más contraste lo necesita también en la
 * pantalla de acceso**, o sea antes de que exista una sesión de la que leer nada. Cuando se
 * guarden en el perfil, el orden será éste: se pintan las locales al cargar y las del
 * servidor ganan en cuanto llegan.
 *
 * HAY UN SOLO EJEMPLAR Y TODOS SE APUNTAN A ÉL. Aquí había un `useState` por componente, así
 * que las nueve pantallas que preguntan tenían nueve copias leídas al montar: Ajustes cambiaba
 * la suya, la guardaba, y las otras ocho seguían con la de antes hasta recargar. El almacén y
 * su prueba viven en `lib/almacenDePreferencias.mjs`, que es donde está contado el caso.
 */
const almacen = creaAlmacen(window.localStorage, window);

export function usePreferencias() {
  const prefs = useSyncExternalStore(almacen.suscribe, almacen.ahora, almacen.ahora);

  // EL AJUSTE MANDA SOBRE LO QUE SUENA, y baja desde aquí. `sound` lo lee también al arrancar
  // -antes de que React monte- para que el primer clic ya salga bien; esto es lo que hace que
  // un cambio en Ajustes se note sin recargar.
  //
  // Y AQUI SOLO SE APUNTA, no se arranca ni se para nada. Estuvo a punto de llamar también a
  // la música, y habría sido un fallo con truco: este gancho lo usan varias pantallas, así que
  // el efecto corre cada vez que monta cualquiera de ellas, y «devolverle el sonido a la
  // música» significa **volver a arrancarla**. Bastaba con ir del Cielo al Ágora para que la
  // música del Cielo empezara a sonar en el Ágora. La música la manda su propio gancho, que
  // vive donde suena.
  useEffect(() => {
    sound.aplica(prefs.sonido);
  }, [prefs.sonido]);

  useEffect(() => {
    const raiz = document.documentElement;
    const puestos = atributos(prefs);
    // Se quitan TODOS los `data-` de preferencias antes de poner los nuevos. Si solo se
    // pusieran, volver a un valor por defecto -que no escribe atributo- dejaría el anterior
    // pegado y el ajuste no se podría deshacer.
    for (const a of [...raiz.attributes]) {
      if (a.name.startsWith("data-") && !(a.name in puestos)) {
        if (["data-movimiento", "data-texto", "data-tema", "data-reloj", "data-sonido",
          "data-fondo"].includes(a.name)) {
          raiz.removeAttribute(a.name);
        }
      }
    }
    for (const [k, v] of Object.entries(puestos)) raiz.setAttribute(k, v);
  }, [prefs]);

  const cambia = useCallback((siguientes) => { almacen.cambia(siguientes); }, []);

  return [prefs, cambia];
}
